import {Component, DestroyRef, inject, OnInit} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {RouterLink} from '@angular/router';
import {NgClass} from '@angular/common';
import {TranslatePipe} from '@ngx-translate/core';
import {AuthenticationService} from '../../../../core/service/auth.service';
import {LanguageService} from '../../../../core/i18n/language.service';
import {Language} from '../../../../core/i18n/language.model';
import {ThemeService} from '../../../../core/service/theme.service';
import {GgDropdownComponent, GgDropdownMenuDirective, GgDropdownToggleDirective} from '../../../../shared/components/gg-dropdown';
import {IconComponent} from '../../../../shared/components/icon/icon.component';
import {version} from '../../../../../../package.json';
import {environment} from '../../../../../environments/environment';
import {MockToolbarService} from '../../../../shared/components/mock-toolbar/mock-toolbar.service';
import {ModalService} from '../../../../shared/modal';
import {UsageStore} from '../../../../shared/statistics/usage.store';

const PLAN_KEYS = ['free', 'basic', 'advanced', 'top'] as const;

/** `guideglare_zero|basic|advanced|top` → klíč nejvyššího plánu (`zero` se v překladech jmenuje `free`). */
function highestPlanKey(subs: string[]): string | null {
    const ranks = subs
        .map(s => s.replace('guideglare_', '').replace('zero', 'free'))
        .map(k => PLAN_KEYS.indexOf(k as typeof PLAN_KEYS[number]))
        .filter(i => i >= 0);
    return ranks.length ? PLAN_KEYS[Math.max(...ranks)] : null;
}

@Component({
    selector: 'app-sidebar-profile-dropdown',
    templateUrl: './sidebar-profile-dropdown.component.html',
    styleUrls: ['./sidebar-profile-dropdown.component.scss'],
    standalone: true,
    imports: [RouterLink, NgClass, TranslatePipe, GgDropdownComponent, GgDropdownToggleDirective, GgDropdownMenuDirective, IconComponent],
})
export class SidebarProfileDropdownComponent implements OnInit {
    readonly appVersion = version;
    readonly isMock = environment.useMockBackend;
    readonly mockToolbar = inject(MockToolbarService);
    private readonly modalService = inject(ModalService);
    protected readonly usage = inject(UsageStore);
    loggedInUser: any = {};
    /** Nejvyšší aktivní plán (název se nepřekládá, zobrazí se verzálkami); null = bez předplatného. */
    planKey: string | null = null;
    langCollapsed = true;

    /** Na desktopu menu vyjede posunuté doprava; v mobilním draweru by přetekl okraj obrazovky. */
    get menuPlacement(): string {
        return typeof matchMedia !== 'undefined' && matchMedia('(min-width: 1024px)').matches ? 'top-start-inset' : 'top-start';
    }

    private readonly destroyRef = inject(DestroyRef);

    constructor(
        private readonly authService: AuthenticationService,
        private readonly languageService: LanguageService,
        readonly themeService: ThemeService,
    ) {}

    ngOnInit(): void {
        this.loggedInUser = this.authService.currentUser();
        this.authService.getSubscriptionChanges()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(subs => {
                this.planKey = highestPlanKey(subs ?? []);
            });
    }

    get languages(): Language[] {
        return this.languageService.getSupportedLanguages();
    }

    get currentLanguage(): Language {
        return this.languageService.getCurrentLanguageSnapshot();
    }

    setLanguage(lang: Language): void {
        this.languageService.setLanguage(lang, true);
    }

    getUserInitials(): string {
        if (this.loggedInUser?.displayName) {
            const names = this.loggedInUser.displayName.trim().split(' ');
            if (names.length >= 2) return names[0].charAt(0) + names[1].charAt(0);
            return names[0].charAt(0) + (names[0].charAt(1) || '');
        }
        if (this.loggedInUser?.email) return this.loggedInUser.email.substring(0, 2);
        return 'U';
    }

    logout(): void {
        this.authService.logout(true);
    }

    /** Modal je lazy — do hlavního bundlu nic nepřidá (spec §4.4.1). */
    async openLicenses(): Promise<void> {
        const {ThirdPartyLicensesModalComponent} = await import('../../../../shared/components/third-party-licenses-modal/third-party-licenses-modal.component');
        this.modalService.open(ThirdPartyLicensesModalComponent, {size: 'lg', centered: true});
    }
}
